import { useLang } from '@/context/LanguageContext';
import { motion } from 'framer-motion';

const MADISON_IMG = 'https://customer-assets.emergentagent.com/job_madison-fred-design/artifacts/ulb30jfw_Copilot_20260415_124803.png';
const FRED_IMG = 'https://customer-assets.emergentagent.com/job_madison-fred-design/artifacts/m61yj8gm_Copilot_20260415_135611.png';

export default function About() {
  const { t } = useLang();
  const stats = t('about.stats') || [];
  const founders = t('about.founders') || [];

  return (
    <section id="about" data-testid="about-section" className="py-24 lg:py-32 bg-zinc-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-4"
        >
          {t('about.label')}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-5xl lg:text-6xl font-normal tracking-normal text-purple-400 mb-16"
          style={{ fontFamily: "'Great Vibes', cursive" }}
        >
          {t('about.title')}
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 mb-24">
          <div className="space-y-6">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed"
            >
              {t('about.p1')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed"
            >
              {t('about.p2')}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-base lg:text-lg font-manrope text-zinc-400 leading-relaxed"
            >
              {t('about.p3')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="flex gap-12 pt-8 justify-center"
            >
              {stats.map((stat, i) => (
                <div key={i} data-testid={`stat-${i}`} className="text-center">
                  <p className="font-outfit text-3xl lg:text-4xl font-black text-zinc-50">{stat.num}</p>
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mt-1 -ml-5">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Founders */}
          <div className="grid grid-cols-2 gap-6">
            {[
              { img: MADISON_IMG, ...founders[0] },
              { img: FRED_IMG, ...founders[1] },
            ].map((founder, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.2, duration: 0.8 }}
                data-testid={`founder-${i}`}
                className="group"
              >
                <div className="overflow-hidden mb-5 aspect-[3/4]">
                  <img
                    src={founder.img}
                    alt={founder.name || ''}
                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-700 scale-[1.02] group-hover:scale-100"
                  />
                </div>
                <p className="font-outfit text-xl font-bold text-zinc-50 tracking-tight">{founder.name}</p>
                <p className="text-sm font-manrope text-zinc-500 mt-1">{founder.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
